<template>
  <div class="bg-white p-4" v-if="productConfig">
    <!-- Fuel prices table -->
    <div class="row" :class="{ 'p-4': !$breakpoint.mdAndDown }">
      <div class="col-md-10 table-container">
        <marketPrices
          v-if="productPrices.length > 0"
          :productData="orderedProductIds"
          :productPrices="productPrices"
          :isPricingEngine="true"
        />
      </div>

      <div class="col-md-2 text-center">
        <button
          class="btn btn-confirm w-100"
          :disabled="isFormCompletionPrevented"
          @click="$emit('saveGlobalPricingRules')"
        >
          Save Changes
        </button>
        <div v-if="Object.keys(savingStatus).length > 0" class="mt-3">
          <i v-if="savingStatus.rulesSaved" class="fas fa-check text-t_green mr-2"></i>
          <span :class="{ 'text-t_red': !savingStatus.rulesSaved }">{{ savingStatus.message }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import breakpoint from "@/mixins/index";
import { mapGetters } from "vuex";
import {
  getBaseSettlementId,
  getStraightProductId,
  getBioProductId,
  getProductDensityReferenceId,
  getProductDutyReferenceId,
} from "@/util/products";
import { calculateFlatPrice, calculateBlendedFlatPrice, calculateLivePrice } from "@/util/calculations";
import { rulesEnum } from "@/util/enums/rules";
import marketPrices from "@/components/marketPrices/marketPrices.vue";

export default {
  components: {
    marketPrices,
  },
  mixins: [breakpoint],
  props: {
    products: {
      type: Array,
      required: true,
    },
    settlementRules: {
      type: Array,
      required: true,
    },
    productRules: {
      type: Array,
      required: true,
    },
    iceReferences: {
      type: Object,
      required: true,
    },
    isFormCompletionPrevented: {
      type: Boolean,
      default: false,
    },
    savingStatus: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      clearSavingStatusTimeout: 0,
      pageSize: 5,
      page: 1,
    };
  },
  methods: {
    setPage(val) {
      this.page = val;
    },
    nextPage() {
      if (this.page >= 1) {
        this.page = this.page + 1;
      }
    },
    previousPage() {
      if (this.page <= this.getPageCount) {
        this.page = this.page - 1;
      }
    },
  },
  computed: {
    ...mapGetters("metadata", ["productConfig"]),
    ...mapGetters("livePrices", ["getPricesUpdate"]),
    orderedProductIds() {
      if (this.productPrices.length > 0) {
        return this.productPrices.map((x) => x.fuel_id);
      } else {
        return [];
      }
    },
    pagedProductData() {
      let data = this.productPrices;
      if (data && data.length > 0) {
        return data.slice(this.pageSize * this.page - this.pageSize, this.pageSize * this.page);
      }
      return [];
    },
    getPageCount() {
      return Math.ceil(this.productPrices.length / this.pageSize);
    },
    getNextPageCount() {
      if (this.page < this.getPageCount - 1) {
        return this.pageSize;
      } else if (this.page === this.getPageCount) {
        return 0;
      } else {
        return this.productPrices.slice(
          this.pageSize * this.getPageCount - this.pageSize,
          this.pageSize * this.getPageCount
        ).length;
      }
    },
    productPrices: {
      get: function () {
        if (
          this.products &&
          this.settlementRules &&
          this.productRules &&
          this.iceReferences &&
          this.getPricesUpdate.length > 0
        ) {
          const productList = this.products.map((p) => {
            return { fuel_id: p._id, name: p.name, isBlended: p.isBlended };
          });

          // Get the FX settlement
          const fxSettlementRule = this.settlementRules.find(
            (r) => r.action.targetAttribute === rulesEnum.eTargetAttributeType.Fx
          );
          const fxSettlement = fxSettlementRule ? Number(fxSettlementRule.action.value) : 0;

          // Get the RTFO's bio-element percentage
          const bioBlendRule = this.productRules.find(
            (r) => r.action.targetAttribute === rulesEnum.eTargetAttributeType.BioBlend
          );
          const bioBlend = bioBlendRule ? Number(bioBlendRule.action.value) : 0;
          const bioBlendPercentage = bioBlend / 100; // Bio blend is divided by 100 to turn it into a percentage

          // Now let's calculate the settle price, market change, and live prices in ppl
          for (let product of productList) {
            let { settlePrice, usdDiff, marketChange, livePrice } = 0;

            // Assign settle and live ICE, as well as live FX
            const iceSettlement = this.iceReferences.settleSymbol
              ? this.getPricesUpdate.find((p) => p.symbol === this.iceReferences.settleSymbol).settle
              : 0;
            let iceLive = this.iceReferences.liveSymbol
              ? this.getPricesUpdate.find((p) => p.symbol === this.iceReferences.liveSymbol).price
              : 0;
            iceLive += this.iceReferences.livePremium;
            const fxLive = this.getPricesUpdate.find((p) => p.symbol === "#X.SGBPUSD").price || 0;

            // We calculate blended/non-blended products' settle/flat prices differently
            if (product.isBlended) {
              // Calculate this product's settle/flat price using its base product's and bio product's settle prices
              const straightProduct = productList.find(
                (p) => p.fuel_id === getStraightProductId(this.productConfig, product.fuel_id)
              );
              const bioProduct = productList.find(
                (p) => p.fuel_id === getBioProductId(this.productConfig, product.fuel_id)
              );
              settlePrice = calculateBlendedFlatPrice(
                bioBlendPercentage,
                bioProduct.settlePrice,
                straightProduct.settlePrice
              );

              // Calculate this product's market change using its base product's and bio product's market changes
              product.settlePrice = Number.isFinite(settlePrice) ? Number(settlePrice) : 0;
              product.marketChange = calculateBlendedFlatPrice(
                bioBlendPercentage,
                bioProduct.marketChange,
                straightProduct.marketChange
              );
            } else {
              // Get the base product's settlement
              const baseProductSettlementRule = this.settlementRules.find(
                (r) =>
                  r.action.targetAttribute === rulesEnum.eTargetAttributeType.ProductSettle &&
                  r.targets[0].ids[0] === getBaseSettlementId(this.productConfig, product.fuel_id)
              );
              const baseProductSettlement = baseProductSettlementRule
                ? Number(baseProductSettlementRule.action.value)
                : 0;

              // Get the product's density
              const densityRule = this.productRules.find(
                (r) =>
                  r.action.targetAttribute === rulesEnum.eTargetAttributeType.Density &&
                  r.targets[0].ids[0] === getProductDensityReferenceId(this.productConfig, product.fuel_id)
              );
              const density = densityRule ? Number(densityRule.action.value) : 0;

              // Get the product's duty
              const dutyRule = this.productRules.find(
                (r) =>
                  r.action.targetAttribute === rulesEnum.eTargetAttributeType.Duty &&
                  r.targets[0].ids[0] === getProductDutyReferenceId(this.productConfig, product.fuel_id)
              );
              const duty = dutyRule ? Number(dutyRule.action.value) : 0;

              // Calculate settle/flat price for this product
              settlePrice = calculateFlatPrice(baseProductSettlement, fxSettlement, density, duty);
              product.settlePrice = Number.isFinite(settlePrice) ? Number(settlePrice) : 0;

              // Calculate differential in USD
              usdDiff = baseProductSettlement - iceSettlement;
              product.usdDiff = Number.isFinite(usdDiff) ? Number(usdDiff) : 0;

              // Calculate market change
              marketChange = calculateFlatPrice(iceLive + product.usdDiff, fxLive, density, duty) - product.settlePrice;
              product.marketChange = Number.isFinite(marketChange) ? Number(marketChange) : 0;
            }

            product.livePrice = calculateLivePrice(product.settlePrice, product.marketChange);
          }

          // Now let's sort the list of products according to how we want to display them and also hide Biodiesel for Gasoil from the list
          const sortedProductList = [
            productList.find((p) => p.fuel_id === this.productConfig.DieselId),
            productList.find((p) => p.fuel_id === this.productConfig.BioDieselId),
            productList.find((p) => p.fuel_id === this.productConfig.BlendedDieselBioId),
            productList.find((p) => p.fuel_id === this.productConfig.GasoilId),
            productList.find((p) => p.fuel_id === this.productConfig.BlendedGasoilId),
            productList.find((p) => p.fuel_id === this.productConfig.KeroseneId),
            productList.find((p) => p.fuel_id === this.productConfig.PetrolId),
            productList.find((p) => p.fuel_id === this.productConfig.PetrolWithAdditiveId),
            productList.find((p) => p.fuel_id === this.productConfig.SU97Id),
            productList.find((p) => p.fuel_id === this.productConfig.SU99Id),
            productList.find((p) => p.fuel_id === this.productConfig.DieselWithAdditiveId),
            productList.find((p) => p.fuel_id === this.productConfig.GasoilFame0Id),
            productList.find((p) => p.fuel_id === this.productConfig.HVOId),
            productList.find((p) => p.fuel_id === this.productConfig.MarineGasoilId),
            productList.find((p) => p.fuel_id === this.productConfig.HighBioMarineDieselId),
            productList.find((p) => p.fuel_id === this.productConfig.PremiumDieselId),
          ].filter((entry) => entry != null);
          return sortedProductList;
        }
        return true;
      },
    },
    hideSaveSuccessfulMessage: function () {
      if (Object.keys(this.savingStatus).length > 0) {
        this.clearSavingStatusTimeout = setTimeout(() => {
          this.$emit("updateGlobalRulesSavingStatus", {});
        }, 4000);
      }
      return true;
    },
  },
  watch: {
    hideSaveSuccessfulMessage() {},
  },
  beforeDestroy() {
    clearTimeout(this.clearSavingStatusTimeout);
    this.$emit("displayGlobalRulesSavedMessage", {});
  },
};
</script>

<style lang="scss" scoped>
table {
  th,
  td {
    width: 14.29%; // 100%/7 columns
  }
}
</style>